import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { LoadingWrapper } from '@linetweet/linetweet-ui';
import { Container } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectCurrentStore } from '../../store/store/selectors';
import { TimeHelper } from '../../utils';
import { getEventStats } from '../../store/eventStats/thunks';
import { ReportsHeader, ReportsChart } from './components';
import { buildServiceStatsChart } from './lib';
import { ReportChartViewTypeEnum } from './types';

import styles from './Reports.module.scss';

type Props = {
  onClose: () => void;
};

const emptyChartData = {
  [ReportChartViewTypeEnum.WEEK]: { labels: [], datasets: [] },
  [ReportChartViewTypeEnum.MONTH]: { labels: [], datasets: [] },
};

const titleByChartViewMap: Record<ReportChartViewTypeEnum, string> = {
  [ReportChartViewTypeEnum.WEEK]: 'Weekly Report',
  [ReportChartViewTypeEnum.MONTH]: 'Monthly Report',
};

export function Reports({ onClose }: Props) {
  const dispatch = useAppDispatch();

  const [chartView, setChartView] = useState<ReportChartViewTypeEnum>(ReportChartViewTypeEnum.WEEK);

  const store = useAppSelector(selectCurrentStore);
  const eventStatsState = useAppSelector((state) => state.eventStats, shallowEqual);

  const params = useMemo(() => {
    const slotBalancerSettings = store.settings.slotBalancerSettings!;

    const firstService = store.services.find((service) => service.name === slotBalancerSettings.firstServiceName)!;
    const followupService = store.services.find((service) => service.name === slotBalancerSettings.followupServiceName)!;

    return {
      pastDays: slotBalancerSettings.calculationTimeFrames.pastDays,
      futureDays: slotBalancerSettings.calculationTimeFrames.futureDays,
      firstService,
      followupService,
    };
  }, [store]);

  const dateRange = useMemo(() => {
    const startOfDay = TimeHelper.getStartOfDay(Date.now(), store.timezone);

    return {
      from: TimeHelper.subtractDays(startOfDay, params.pastDays, store.timezone),
      to: TimeHelper.addDays(startOfDay, params.futureDays, store.timezone),
    };
  }, [store.timezone, params.pastDays, params.futureDays]);

  const eventStatsData = useMemo(() => {
    if (!eventStatsState.queryHaveResponse[`${dateRange.from}-${dateRange.to}`]) {
      return emptyChartData;
    }

    return buildServiceStatsChart({
      data: eventStatsState.data,
      dateRange,
      firstService: params.firstService,
      followupService: params.followupService,
      timezone: store.timezone,
    });
  }, [eventStatsState, dateRange, params.firstService, params.followupService, store.timezone]);

  useEffect(() => {
    if (!eventStatsState.queryHaveResponse[`${dateRange.from}-${dateRange.to}`]) {
      dispatch(getEventStats({
        from: dateRange.from,
        to: dateRange.to,
        storeId: store.id,
        enterpriseId: store.enterpriseId,
      }));
    }
  }, [dispatch, dateRange, store.id, store.enterpriseId, store.timezone]);

  return (
    <div className={styles.reports_wrapper}>
      <ReportsHeader
        title={titleByChartViewMap[chartView]}
        onBackClick={onClose}
        chartView={chartView}
        onChartViewChange={setChartView}
      />

      <div className={styles.reports_container}>
        <LoadingWrapper loading={eventStatsState.loading}>
          <Container sx={{ overflowY: 'auto' }}>
            <ReportsChart
              data={eventStatsData[chartView]}
            />
          </Container>
        </LoadingWrapper>
      </div>
    </div>
  );
}
